import CloseIcon from '@mui/icons-material/Close'
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined'
import { Badge, FormControlLabel, IconButton, Popover, Switch } from '@mui/material'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Link } from 'react-router-dom'
import { useNavigate } from 'react-router-dom-v5-compat'
import * as api from 'shared/api/notifications'
import { Button } from 'shared/components/Button/Button'
import { TextV2 } from 'shared/components/Text/TextV2'
import { Timestamp } from 'shared/components/Text/Timestamp'
import { CompanyUser } from 'shared/context/CompanyContext'
import { TYPOGRAPHY } from 'shared/theme/typographyV2'

import {
  canSeeNotifications,
  notificationKey,
  notificationPolling,
  useReadNotifications,
  useUnreadNotifications,
} from './useNotifications'

const labels: Record<api.NotificationType, string> = {
  INBOX_EMAIL: 'Inbox email',
  ISSUE_ASSIGNED: 'Assigned to you',
}
const preferenceLabels: Record<api.NotificationType, string> = {
  INBOX_EMAIL: 'New Inbox email',
  ISSUE_ASSIGNED: 'Issues assigned to me',
}
const linkClass = 'text-royal-blue underline underline-offset-2 focus-visible:outline focus-visible:outline-2'

export function UnreadDot() {
  return (
    <span className="inline-flex shrink-0 items-center">
      <span aria-hidden="true" className="bg-royal-blue h-2 w-2 rounded-full" />
      <span className="sr-only">Unread</span>
    </span>
  )
}

export function NotificationBell({ user }: { user: CompanyUser }) {
  return canSeeNotifications(user) ? <Bell user={user} /> : null
}

function Bell({ user }: { user: CompanyUser }) {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null)
  const panelId = `notifications-${user.id}`
  const titleId = `${panelId}-title`
  const navigate = useNavigate()
  const cache = useQueryClient()
  const count = useUnreadNotifications(user)
  const read = useReadNotifications(user)
  const key = notificationKey(user)
  const list = useQuery([...key, 'list'], api.getNotifications, {
    ...notificationPolling,
    enabled: !!anchor,
  })
  const preferences = useQuery([...key, 'preferences'], api.getNotificationPreferences, {
    enabled: !!anchor,
    retry: false,
  })
  const preference = useMutation(api.setNotificationPreference, {
    onSuccess: (data) => {
      cache.setQueryData([...key, 'preferences'], data)
    },
  })
  const close = () => setAnchor(null)
  async function openNotification(item: api.Notification) {
    if (read.isLoading) return
    try {
      if (item.unread) await read.mutateAsync({ ids: [item.id] })
      close()
      navigate(item.link)
    } catch {
      // Keep the row available so the user can retry the read and navigation together.
    }
  }
  return (
    <>
      <IconButton
        aria-label={count.data ? `Notifications, ${count.data.count} unread` : 'Notifications'}
        aria-expanded={!!anchor}
        aria-controls={anchor ? panelId : undefined}
        aria-haspopup="dialog"
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        <Badge badgeContent={count.data?.count} max={99} color="primary">
          <NotificationsOutlinedIcon />
        </Badge>
      </IconButton>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          id: panelId,
          role: 'dialog',
          'aria-labelledby': titleId,
          className: 'flex w-96 max-w-full flex-col',
        }}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 p-4">
          <TextV2 id={titleId} type={TYPOGRAPHY.header3}>
            Notifications
          </TextV2>
          <IconButton aria-label="Close notifications" onClick={close}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className="shrink-0 px-4 pb-4">
          <Button
            size="small"
            variant="secondary"
            ariaDisabled={read.isLoading || !count.data?.count}
            onClick={() => read.mutate({ all: true })}
          >
            Mark all read
          </Button>
          {read.isLoading && (
            <TextV2 className="mt-3" type={TYPOGRAPHY.paragraph2}>
              Marking read…
            </TextV2>
          )}
          {read.isError && (
            <TextV2 className="mt-3" type={TYPOGRAPHY.paragraph2}>
              Couldn’t mark notifications as read. Try the action again.
            </TextV2>
          )}
          {count.isError && (
            <TextV2 className="mt-3" type={TYPOGRAPHY.paragraph2}>
              Unread count is unavailable.{' '}
              <Button size="small" variant="text" onClick={() => count.refetch()}>
                Retry count
              </Button>
            </TextV2>
          )}
        </div>
        {list.isLoading && <TextV2 className="px-4 py-6">Loading notifications…</TextV2>}
        {list.isError && (
          <div className="px-4 py-6">
            <TextV2 className="mb-3">Couldn’t load notifications.</TextV2>
            <Button size="small" variant="secondary" onClick={() => list.refetch()}>
              Retry notifications
            </Button>
          </div>
        )}
        {list.data?.length === 0 && (
          <div className="px-4 py-6">
            <TextV2 type={TYPOGRAPHY.label2} className="mb-2">
              No notifications yet
            </TextV2>
            <TextV2 type={TYPOGRAPHY.paragraph2}>
              New Inbox email and issues assigned to you appear here.
            </TextV2>
          </div>
        )}
        {!!list.data?.length && (
          <ul
            aria-label="Recent notifications"
            className="border-light-gray4 m-0 min-h-24 list-none overflow-y-auto border-0 border-y border-solid bg-white p-0"
          >
            {list.data.map((item) => (
              <li key={item.id} className="border-light-gray4 border-0 border-b border-solid last:border-b-0">
                <button
                  type="button"
                  onClick={() => openNotification(item)}
                  aria-disabled={read.isLoading}
                  className="hover:bg-bg-gray focus-visible:outline-royal-blue block w-full cursor-pointer border-0 bg-transparent p-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2"
                >
                  <TextV2
                    type={TYPOGRAPHY.paragraph3}
                    className="text-mid-gray mb-2 flex flex-wrap items-center gap-x-2 gap-y-1"
                  >
                    <span>{labels[item.type]}</span>
                    <span aria-hidden="true">·</span>
                    <Timestamp value={item.date_created} recent />
                  </TextV2>
                  <div className="flex items-center justify-between gap-3">
                    <TextV2
                      type={TYPOGRAPHY.paragraph2}
                      className={`min-w-0 break-words ${item.unread ? 'font-semibold' : ''}`}
                    >
                      {item.title}
                    </TextV2>
                    {item.unread && <UnreadDot />}
                  </div>
                  <TextV2
                    type={TYPOGRAPHY.paragraph2}
                    className="text-mid-gray mt-1 break-words"
                    title={item.detail}
                  >
                    {item.detail}
                  </TextV2>
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex shrink-0 flex-col items-start gap-4 p-4">
          <nav aria-label="Notification destinations" className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/issues/" onClick={close} className={linkClass}>
              Open Issues
            </Link>
            <Link to="/inbox/" onClick={close} className={linkClass}>
              Open Inbox
            </Link>
          </nav>
          {preferences.isLoading && (
            <TextV2 className="mt-4" type={TYPOGRAPHY.paragraph2}>
              Loading your preferences…
            </TextV2>
          )}
          {preferences.isError && (
            <TextV2 className="mt-4" type={TYPOGRAPHY.paragraph2}>
              Couldn’t load your preferences.{' '}
              <Button size="small" variant="text" onClick={() => preferences.refetch()}>
                Retry preferences
              </Button>
            </TextV2>
          )}
          {!!preferences.data?.length && (
            <div
              role="group"
              aria-labelledby={`${panelId}-preferences`}
              className="flex w-full min-w-0 flex-col"
            >
              <TextV2 id={`${panelId}-preferences`} type={TYPOGRAPHY.label3} className="mb-1">
                Notify me about
              </TextV2>
              {preferences.data.map((item) => (
                <FormControlLabel
                  key={item.type}
                  className="m-0"
                  control={
                    <Switch
                      className={preference.isLoading ? 'opacity-50' : undefined}
                      checked={item.enabled}
                      inputProps={{ 'aria-disabled': preference.isLoading }}
                      onChange={(_, enabled) => {
                        if (!preference.isLoading) preference.mutate({ type: item.type, enabled })
                      }}
                    />
                  }
                  label={<TextV2 type={TYPOGRAPHY.paragraph2}>{preferenceLabels[item.type]}</TextV2>}
                />
              ))}
            </div>
          )}
          {preference.isLoading && (
            <TextV2 className="mt-2" type={TYPOGRAPHY.paragraph2}>
              Saving {preference.variables && preferenceLabels[preference.variables.type]}…
            </TextV2>
          )}
          {preference.isError && (
            <TextV2 className="mt-2" type={TYPOGRAPHY.paragraph2}>
              Couldn’t save your preference. Try the toggle again.
            </TextV2>
          )}
        </div>
      </Popover>
    </>
  )
}
